import { useCallback } from 'react';

import { useAuthStore } from '@/stores/AuthState';
import { changeUserLanguage } from '@/api/user';
import type { LanguageType } from '@/types';

import fr from '@/translations/fr.json';
import en from '@/translations/en.json';

type TranslationValue = string | { [key: string]: TranslationValue };

/**
 * i18n-plural — a key may hold two forms instead of one string:
 *
 *     "hiddenAllTitle": { "one": "…1 son", "other": "…{{count}} sons" }
 *
 * The object shape is deliberate: a translator opening the file SEES that
 * there are two cases. Suffixed keys (`key_one` / `key_other`) would be
 * invisible, and `t()`'s segment walk would mistake them for a path.
 */
export interface PluralForms {
  one: string;
  other: string;
}

export const isPluralForms = (value: unknown): value is PluralForms =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as PluralForms).one === 'string' &&
  typeof (value as PluralForms).other === 'string';

/**
 * fr and en share the same rule — 1 on one side, everything else on the other.
 * `Intl.PluralRules` would be correct but introduces locale-dependent behaviour
 * for two languages that do not need it; revisit the day a language with more
 * forms ships.
 */
export const pickPluralForm = (forms: PluralForms, count: number | undefined): string =>
  count === 1 ? forms.one : forms.other;
type TranslationsType = Record<string, TranslationValue>;

const translations: Record<string, TranslationsType> = { fr, en };

/**
 * Translation hook — pure-TS, reads the active language from the auth store.
 * Ported unchanged from the legacy app (one of the easy migration wins).
 */
export const useTranslation = () => {
  const language = useAuthStore((state) => state.userInfo.language) || 'en';

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      const file = translations[language] || translations.en;
      const keys = key.split('.');
      let translation = keys.reduce<TranslationValue | undefined>(
        (obj, k) => (obj && typeof obj === 'object' ? obj[k] : undefined),
        file,
      );

      // i18n-plural — a plural key resolves to { one, other }; pick before
      // interpolating, never the other way round.
      if (isPluralForms(translation)) {
        translation = pickPluralForm(translation, params?.count as number | undefined);
      }

      if (!translation) {
        if (import.meta.env.DEV) {
          console.warn(`Translation key not found: ${key}`);
        }
        return key;
      }

      if (params) {
        Object.entries(params).forEach(([param, value]) => {
          translation = (translation as string).replace(`{{${param}}}`, String(value));
        });
      }

      return translation as string;
    },
    [language],
  );

  const changeLanguage = useCallback(async (newLanguage: LanguageType) => {
    const { setUserInfo, userInfo } = useAuthStore.getState();
    const previousLanguage = userInfo.language;

    // Optimistic local update for instant UI reactivity.
    await setUserInfo({ ...userInfo, language: newLanguage });

    if (userInfo.id) {
      try {
        await changeUserLanguage(newLanguage);
      } catch (error) {
        const { userInfo: current } = useAuthStore.getState();
        await setUserInfo({ ...current, language: previousLanguage });

        console.warn('Error changing language on server:', error);
      }
    }
  }, []);

  return { t, language, changeLanguage };
};
